import {IFacilityType} from '../pages/generate-report/types';

// ============================================================================
// File Processing Types - Optimized
// ============================================================================

/**
 * Backend file upload status (from database)
 * Using string literals instead of enums for smaller bundle size
 */
export type FileUploadStatus =
    | 'queued'
    | 'assembling'
    | 'uploading'
    | 'uploaded'
    | 'validating'
    | 'processing'
    | 'completed'
    | 'failed'
    | 'deleted';

/**
 * Frontend-friendly file status for UI
 */
export type FileStatus = 'uploading' | 'needs-review' | 'processing' | 'queued' | 'completed';

/**
 * Backend processing event status (from SSE)
 */
export type ProcessingEventStatus =
    | 'started'
    | 'in_progress'
    | 'completed'
    | 'failed'
    | 'warning'
    | 'skipped';

/**
 * Frontend stage status for UI
 */
export type StageStatus = 'complete' | 'failed' | 'pending' | 'processing';

/**
 * Processing stages structure
 */
export interface ProcessingStages {
    fileUpload: StageStatus;
    validation: StageStatus;
    rosterStandardization: StageStatus;
    addressGeocoding: StageStatus;
    distanceAndTimeCalculation: StageStatus;
    providerAddressMapping: StageStatus;
    rosterAndSpecialtySetup: StageStatus;
    complianceCalculation: StageStatus;
    summaryGeneration: StageStatus;
    completion: StageStatus;
}

/**
 * Frontend stage keys - derived from ProcessingStages (single source of truth)
 */
export type StageKey = keyof ProcessingStages;

/**
 * Stage groups for logical organization (string literal type)
 */
export type StageGroup =
    | 'validation'
    | 'roster_ingestion'
    | 'roster_setup'
    | 'geocoding'
    | 'compliance_reporting';

export interface ActivityItem {
    time: string; // Formatted display time (e.g., "Apr 17, 2026 9:30 AM")
    message: string;
    timestamp: string; // ISO 8601 timestamp for reliable sorting
    startTime?: string;
    endTime?: string;
    duration?: string;
}

export interface ErrorDetails {
    issuesFound: string;
    rowsWithIssues: number;
    cellsWithIssues: number;
    rowsExcluded?: number;
    errors: string[];
}

export interface UploadDetails {
    uploadedAt: string;
    fileSize: string;
    uploadedBy: string;
}

export interface ProcessingFile {
    id: string;
    fileName: string;
    originalFileName?: string;
    dateUploaded: string;
    uploadedBy: string;
    status: FileStatus;
    stage: string;
    stages: ProcessingStages;
    activity: ActivityItem[];
    uploadDetails: UploadDetails;
    errorDetails?: ErrorDetails;
    errorReportFile?: string | null;
    isNew?: boolean;
    detailsLoaded?: boolean;
}

/**
 * File processing event from SSE stream
 * Note: Backend sends numeric fields as strings in SSE events
 */
export interface FileProcessingEvent {
    id: number | string; // Backend sends as string, convert to number when needed
    stage_name: string;
    status: ProcessingEventStatus;
    message: string;
    event_ts: string;
    details?: {
        duration_seconds?: number;
        validation_errors?: number;
        validation_warnings?: number;
        [key: string]: any;
    };
    duration_seconds?: number | string; // Backend may send as string
    stage_group?: string; // Additional field from backend
    stage_sequence?: number | string; // Additional field from backend
    task_name?: string | null; // Additional field from backend
}

// ============================================================================
// Constants - Includes const objects for IDE autocomplete
// ============================================================================

/**
 * File status constants - provides IDE autocomplete
 */
export const FILE_STATUS = {
    NEEDS_REVIEW: 'needs-review',
    PROCESSING: 'processing',
    QUEUED: 'queued',
    COMPLETED: 'completed'
} as const satisfies Record<string, FileStatus>;

/**
 * Stage status constants - provides IDE autocomplete
 */
export const STAGE_STATUS = {
    COMPLETE: 'complete',
    FAILED: 'failed',
    PENDING: 'pending',
    PROCESSING: 'processing'
} as const satisfies Record<string, StageStatus>;

/**
 * Empty stages template (use with spread operator)
 */
export const EMPTY_STAGES: ProcessingStages = {
    fileUpload: 'pending',
    validation: 'pending',
    rosterStandardization: 'pending',
    addressGeocoding: 'pending',
    distanceAndTimeCalculation: 'pending',
    providerAddressMapping: 'pending',
    rosterAndSpecialtySetup: 'pending',
    complianceCalculation: 'pending',
    summaryGeneration: 'pending',
    completion: 'pending'
} as const;

/**
 * Stage display names for UI
 */
const STAGE_DISPLAY_NAMES: Record<StageKey, string> = {
    fileUpload: 'File Upload',
    validation: 'Validation',
    rosterStandardization: 'Roster Standardization',
    addressGeocoding: 'Address Geocoding',
    distanceAndTimeCalculation: 'Distance & Time Calculation',
    providerAddressMapping: 'Provider Address Mapping',
    rosterAndSpecialtySetup: 'Roster & Specialty Setup',
    complianceCalculation: 'Compliance Calculation',
    summaryGeneration: 'Summary Generation',
    completion: 'Completion'
} as const;

/**
 * Stage order (for progress tracking)
 */
export const STAGES_ORDER: readonly StageKey[] = [
    'fileUpload',
    'validation',
    'rosterStandardization',
    'addressGeocoding',
    'distanceAndTimeCalculation',
    'providerAddressMapping',
    'rosterAndSpecialtySetup',
    'complianceCalculation',
    'summaryGeneration',
    'completion'
] as const;

/**
 * Mapping of individual stages to their stage groups
 */
export const STAGE_TO_GROUP_MAP: Record<StageKey, StageGroup> = {
    fileUpload: 'validation',
    validation: 'validation',
    rosterStandardization: 'roster_ingestion',
    addressGeocoding: 'geocoding',
    distanceAndTimeCalculation: 'geocoding',
    providerAddressMapping: 'geocoding',
    rosterAndSpecialtySetup: 'roster_setup',
    complianceCalculation: 'compliance_reporting',
    summaryGeneration: 'compliance_reporting',
    completion: 'compliance_reporting'
} as const;

/**
 * Ordered list of all stage groups
 */
export const STAGE_GROUPS_ORDER: readonly StageGroup[] = [
    'validation',
    'roster_ingestion',
    'geocoding',
    'roster_setup',
    'compliance_reporting'
] as const;

// ============================================================================
// Mapping Functions - Consolidated & Optimized
// ============================================================================

/**
 * Convert snake_case to camelCase (for programmatic stage name mapping)
 */
function snakeToCamel(str: string): string {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Status mapping - Single source of truth
 */
export const STATUS_MAPS = {
    /**
     * Backend file status → Frontend file status
     */
    fileStatus(status: string): FileStatus {
        const normalized = status.toLowerCase();

        if (['queued', 'assembling', 'uploading', 'uploaded'].includes(normalized)) {
            return FILE_STATUS.QUEUED;
        }
        if (['validating', 'processing'].includes(normalized)) {
            return FILE_STATUS.PROCESSING;
        }
        if (['completed', 'deleted'].includes(normalized)) {
            return FILE_STATUS.COMPLETED;
        }
        if (normalized === 'failed') {
            return FILE_STATUS.NEEDS_REVIEW;
        }

        return FILE_STATUS.QUEUED; // fallback
    },

    /**
     * Backend event status → Frontend stage status
     */
    stageStatus(status: string): StageStatus {
        const normalized = status.toLowerCase();

        if (['started', 'in_progress', 'processing', 'running', 'queued'].includes(normalized)) {
            return STAGE_STATUS.PROCESSING;
        }
        if (['completed', 'complete', 'success', 'warning'].includes(normalized)) {
            return STAGE_STATUS.COMPLETE;
        }
        if (['failed', 'error'].includes(normalized)) {
            return STAGE_STATUS.FAILED;
        }
        if (normalized === 'skipped') {
            return STAGE_STATUS.PENDING;
        }

        return STAGE_STATUS.PENDING; // fallback
    },

    /**
     * Backend stage name → Frontend stage key (programmatic conversion)
     */
    stageName(stage: string): StageKey | null {
        const camelCase = snakeToCamel(stage);

        // Validate it's a valid stage key
        if (camelCase in EMPTY_STAGES) {
            return camelCase as StageKey;
        }

        return null;
    }
} as const;

/**
 * Get stage display name
 */
export function getStageDisplayName(stage: StageKey): string {
    return STAGE_DISPLAY_NAMES[stage] || stage;
}

/**
 * Get all stages belonging to a specific group
 */
export function getStagesByGroup(group: StageGroup): StageKey[] {
    return Object.entries(STAGE_TO_GROUP_MAP)
        .filter(([, stageGroup]) => stageGroup === group)
        .map(([stage]) => stage as StageKey);
}

// ============================================================================
// Geography & Network Analysis Types
// ============================================================================

export type GeographyFilterOptions = {
    county_additional_info: {
        county_name: string;
        county_ssa: string;
        county_designation: string;
        fips: string;
        mapbox_id: string;
        state: string;
        year_key: number;
    };
    county_dim_key: string;
    patient_network_population: string;
    run_id: string;
    ssacd: string;
    standard_type: string;
    patient_roster_id: string;
    patient_roster_name: string;
    standard_version: string;
    state: string;
    state_additional_info: {
        bbox: number[];
        code: string;
        mapbox_id: string;
        state: string;
        state_ssa: string;
    };
};

export type AppliedGeographyFilter = {
    state: string;
    county: string;
    standard_type: string;
    patient_roster_name: string;
    standard_version: string;
};

export type DisruptionAnalysisFilterOptions = {
    county_additional_info: {
        county_name: string;
        county_ssa: string;
        county_designation: string;
        fips: string;
        mapbox_id: string;
        state: string;
        year_key: number;
    };
    ssacd: string;
    standard_type: string;
    patient_roster_id: string;
    patient_roster_name: string;
    state: string;
    zip: string;
    state_additional_info: {
        bbox: number[];
        code: string;
        mapbox_id: string;
        state: string;
        state_ssa: string;
    };
};

export type AppliedDisruptionAnalysisFilter = {
    state: string;
    county: string;
    standard_type: string;
    patient_roster_name: string;
    zip: string;
};

export type ComplianceScore = any;
export type RunId = number;

export type FetchGeoJsonResponse = GeoJSON.FeatureCollection;
export type FetchProvidersResponse = any[];
export type FetchComplianceResponse = Array<any>;
export type FetchRunIdResponse = number | null;

export const DISTANCE_CALC_TYPES = [
    {name: 'Drivetime', value: 'actual'},
    {name: 'Physical', value: 'euclidean'},
];

export type SortOrderItem = {
    direction: 'ASC' | 'DESC' | 'NONE';
    column_name: string;
};

export interface IGeographyFilterPayload {
    state: string;
    county: string;
    standard_version: string;
    standard_type: string;
    patient_roster_id: string;
}

export interface IDisruptionAnalysisFilterPayload {
    state: string;
    county: string;
    standard_version?: string;
    standard_type: string;
    patient_roster_id: string;
    zip: string;
}

export interface IFetchReportsPayload {
    roster_id: number;
    revision_id: number;
    signal: AbortSignal;
    standard_type: string;
    standard_version: string;
    patient_roster_id: number;
    state?: string[];
    county?: string[];
    specialty_code: string[];
    limit?: number;
    page?: number;
    order_by?: SortOrderItem;
}

export enum StandardType {
    QHP = 'QHP',
    MA = 'MA',
}

export interface IFetchSpecialtyGroupingPayload {
    roster_id: number;
    standard_type: string;
    standard_version: string;
    specialty_code: string[];
    signal: AbortSignal;
}

export interface IFetchMaxTimeDistPayload {
    roster_id: number;
    revision_id: number;
    standard_type: string;
    standard_version: string;
    specialty_code: string[];
    signal: AbortSignal;
}

export type IFetchAvgTimeDistPayload = IFetchMaxTimeDistPayload & {
    provider_facility_type: IFacilityType;
};

export type IFetchSpecialtyStdsByCountyPayload = IFetchMaxTimeDistPayload & {
    state?: string
};

export type IFetchSpecialtyStdsByCountyBatchPayload = IFetchMaxTimeDistPayload & {
    state: string[];
    county: string[];
};

// ============================================================================
// API Response Types
// Backend controller responses - use snake_case to match API contract
// ============================================================================

export interface FileListItem {
    id: number;
    filename: string;
    org_id: string; // Backend sends as string
    user_id: string; // Backend sends as string
    uploaded_by?: string;
    status: string;
    current_stage: string;
    stage_group?: StageGroup;
    run_name: string;
    size_bytes: number;
    created_at: string; // ISO format
    processing_completed_at: string | null; // ISO format
    error_message: string | null;
    error_report_file: string | null; // S3 path to error report
}

export interface FileListResponse {
    files: FileListItem[];
    current_page: number;
    total_pages: number;
    total_rows: number;
    page_size: number;
}

export interface FileEvent {
    id: number;
    file_upload_id: number;
    stage_name: string;
    stage_sequence: number;
    event_ts: string; // ISO format
    status: ProcessingEventStatus; // More specific than generic string
    message: string;
    details_json: object | null;
    task_name: string | null; // Can be null in API responses
    duration_seconds: number | null; // Can be null in API responses
}

/**
 * File processing stream entry stored in sessionStorage
 * Used for tracking active SSE streams per file
 */
export interface FileProcessingStreamEntry {
    file_upload_id: number;
    file_name: string;
    timestamp: number;
}

export interface FileDetailResponse {
    id: number;
    filename: string;
    original_filename: string;
    org_id: string; // Backend sends as string
    user_id: string; // Backend sends as string
    uploaded_by?: string;
    status: string;
    current_stage: string;
    run_name: string;
    databricks_run_id: number | null;
    size_bytes: number;
    volume_path: string;
    error_report_path: string | null;
    error_message: string | null;
    validation_errors_count: number | null; // Can be null before validation
    validation_warnings_count: number | null; // Can be null before validation
    created_at: string; // ISO format
    updated_at: string | null; // Can be null if never updated
    validation_completed_at: string | null; // ISO format
    processing_started_at: string | null; // ISO format
    processing_completed_at: string | null; // ISO format
    events: FileEvent[];
}

/**
 * File upload job phase tracking
 */
export type UploadJobPhase = 'initializing' | 'uploading' | 'assembling' | 'done' | 'error' | 'interrupted';

export const UPLOAD_JOB_PHASE = {
    INITIALIZING: 'initializing',
    UPLOADING: 'uploading',
    ASSEMBLING: 'assembling',
    DONE: 'done',
    ERROR: 'error',
    INTERRUPTED: 'interrupted',
} as const satisfies Record<string, UploadJobPhase>;

/**
 * File upload job tracking (for chunked uploads)
 */
export type UploadJob = {
    id: string;
    uploadId?: string;
    filename: string;
    fileSize: number;
    phase: UploadJobPhase;
    progress: number;
    totalChunks: number;
    completedChunks: number;
    errorMessage?: string;
    file?: File;
    jobType?: 'upload' | 'resubmit';
    fileId?: string;
};

// Status mapping constants
export const API_STATUS_TO_FILE_STATUS: Record<string, 'queued' | 'processing' | 'completed' | 'needs-review'> = {
    // File upload stages - waiting for processing
    pending: 'queued',
    queued: 'queued',
    waiting: 'queued',
    assembling: 'queued',
    uploading: 'queued',
    uploaded: 'queued',
    // Active processing stages
    validating: 'processing',
    processing: 'processing',
    running: 'processing',
    in_progress: 'processing',
    // Completion states
    completed: 'completed',
    success: 'completed',
    complete: 'completed',
    deleted: 'completed',
    // Error states
    failed: 'needs-review',
    error: 'needs-review'
};

export const EVENT_STATUS_TO_STAGE_STATUS: Record<string, 'complete' | 'failed' | 'processing' | 'pending'> = {
    // Complete states
    complete: 'complete',
    completed: 'complete',
    success: 'complete',
    // Failed states
    failed: 'failed',
    error: 'failed',
    // Processing states (backend: started, in_progress, queued)
    started: 'processing',      // ✅ Backend event status
    processing: 'processing',
    running: 'processing',
    in_progress: 'processing',  // ✅ Backend event status
    queued: 'processing',       // ✅ Backend event status - job queued for processing, show loader
    // Pending states
    pending: 'pending',
    waiting: 'pending',
    // Special states
    warning: 'complete',        // ✅ Backend event status - treat as complete with warning
    skipped: 'pending'          // ✅ Backend event status - treat as pending/skipped
};

// ============================================================================
// Navigation Types
// ============================================================================

export type NavItem = {
    label: string;
    path: string;
    useRosterID: boolean;
    hideTab: boolean;
    disabled?: boolean;
    disabledTooltip?: string;
};
